import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MatLegacyAutocomplete as MatAutocomplete,
  MatLegacyAutocompleteModule,
} from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyOptionModule } from '@angular/material/legacy-core';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ErrorDirective, ErrorsDirective } from '@shared/directives/errors';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { UtilService } from '@shared/services/util.service';
import { ICountry } from '@shared/shared/interfaces/country.interfaces';
import { IMemberProfileAddress } from '@shared/shared/interfaces/member-profile-address.interfaces';
import * as MemberProfileStore from '@shared/store/member-profile';
import { Angulartics2Segment } from 'angulartics2';
import { Observable, Subject, of } from 'rxjs';
import { filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { CountryService } from '../../../services/country.service';

@Component({
  selector: 'ten-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['../popup-form-base.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    NgIf,
    ReactiveFormsModule,
    MatLegacyFormFieldModule,
    MatLegacySelectModule,
    MatLegacyOptionModule,
    ErrorDirective,
    ErrorsDirective,
    MatLegacyInputModule,
    AsyncPipe,
    MatLegacyAutocompleteModule,
    NgFor,
    MatLegacyCheckboxModule,
    MatLegacyButtonModule,
    NgClass,
  ],
})
export class AddressFormComponent implements OnDestroy, OnInit, AfterViewInit {
  @Input() public pastDataId?: any;
  @Output() public closed = new EventEmitter<any>();
  @ViewChild(MatAutocomplete) public countryAutocomplete: MatAutocomplete;

  public member_profile_addresses$: Observable<IMemberProfileAddress[]> = this.ngrxStore.select(
    MemberProfileStore.getMemberProfileAddressesFeatureState
  );

  public addressFormGroup: UntypedFormGroup;
  public newAddress = true;
  public addressToEdit$: Observable<IMemberProfileAddress>;
  private unsubscribe$ = new Subject<void>();
  public countries: Array<ICountry> = [];
  public filteredCountries: Array<ICountry>;
  public invalidCountry: boolean;
  public stateRequired: boolean;
  public customVariables$: Observable<CustomisableVariables> = this.customVariablesService.variables;
  private noSpecialCharsValidator = Validators.pattern(/^[^;<>()*]+$/i);

  constructor(
    private form_builder: UntypedFormBuilder,
    private countryService: CountryService,
    private ngrxStore: Store<MemberProfileStore.MemberProfileState>,
    private changeDetecorRef: ChangeDetectorRef,
    private customVariablesService: CustomisableVariablesService,
    private util: UtilService,
    private angulartics2Segment: Angulartics2Segment
  ) {
    this.addressFormGroup = this.form_builder.group({
      preferred: [false],
      type: ['', Validators.required],
      description: ['', this.noSpecialCharsValidator],
      house_number: ['', [Validators.maxLength(30), this.noSpecialCharsValidator]],
      house_name: [''],
      address_line1: ['', [Validators.required, this.noSpecialCharsValidator]],
      address_line2: ['', this.noSpecialCharsValidator],
      city: ['', [Validators.required, this.noSpecialCharsValidator]],
      state: ['', this.noSpecialCharsValidator],
      country: ['', Validators.required],
      country_url: [''],
      postcode: ['', this.noSpecialCharsValidator],
      filter: [],
    });
  }

  public ngOnInit() {
    this.countryService
      .getCountries()
      .pipe(
        withLatestFrom(this.member_profile_addresses$),
        filter(([, profile_addresses]) => !!profile_addresses),
        take(1),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(([countries, profile_addresses]) => {
        this.countries = countries;
        this.filterAutocomplete();
        this.newAddress = this.pastDataId === undefined;

        if (!this.newAddress) {
          profile_addresses.forEach((value) => {
            if (value.id === this.pastDataId) {
              this.addressToEdit$ = of(value);
              this.obtainCountryData(value);
            }
          });
        }
        this.changeDetecorRef.markForCheck();
      });
  }

  public ngAfterViewInit() {
    this.countryAutocomplete.closed.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.checkCountry();
    });
  }

  /**
   * Loops through the countries array to find the country that matches the one in the
   * passed address. Called only if the user is editing a preiviously stored address
   * @param addressToEdit - The address the user wishes to edit retireved from addresses$
   */
  public obtainCountryData(addressToEdit: IMemberProfileAddress) {
    const requiredCountry = this.countries.filter((country) => country.url.includes(addressToEdit.country))[0].name;
    const newAddress = { ...addressToEdit, country_name: requiredCountry };
    this.restoreFormData(newAddress);
  }

  /**
   * Sets the value of the form fields if form is used to edit a previously saved address. Called from
   * the 'obtainAddressData' function
   * @param data - Address data to be edited
   */
  public restoreFormData(data: IMemberProfileAddress) {
    const countryControl = this.addressFormGroup.get('country');
    this.addressFormGroup.get('type').setValue(data.type);
    this.addressFormGroup.get('description').setValue(data.description);
    this.addressFormGroup.get('house_number').setValue(data.house_number);
    this.addressFormGroup.get('house_name').setValue(data.house_name);
    this.addressFormGroup.get('address_line1').setValue(data.address_line1);
    this.addressFormGroup.get('address_line2').setValue(data.address_line2);
    this.addressFormGroup.get('city').setValue(data.city);
    this.addressFormGroup.get('state').setValue(data.state);
    this.addressFormGroup.get('country_url').setValue(data.country);
    this.addressFormGroup.get('postcode').setValue(data.postcode);
    this.addressFormGroup.get('preferred').setValue(data.preferred);
    this.addressFormGroup.get('filter').setValue(data.filter);
    countryControl.setValue(data['country_name']);

    const country = this.countries.find((item) => item.name === countryControl.value);
    this.setStateValidators(country);
  }

  /**
   * Called once the user chooses a value from the country autocomplete
   * @param country - The url value of the country chosen
   */
  public setChosenCountry(country: string) {
    this.addressFormGroup.controls['country_url'].setValue(country);
    this.changeDetecorRef.markForCheck();
  }

  /**
   * Resets the country form control if entered value is invalid. Called on country input blur
   */
  public checkCountry(event?: FocusEvent) {
    const countryControl = this.addressFormGroup.controls.country;
    const countryUrlControl = this.addressFormGroup.controls.country_url;

    if (this.invalidCountry || countryUrlControl.value === '') {
      countryControl.setValue('');
      countryUrlControl.setValue('');
      this.changeDetecorRef.markForCheck();
    }

    if (!(event && this.countryAutocomplete.isOpen)) {
      const country = this.countries.find((item) => item.name === countryControl.value);
      this.setStateValidators(country);
    }
  }

  /**
   * Filters the total list of countries to find country names that contain characters entered by the user.
   * Called each time the country value changes
   */
  public filterAutocomplete() {
    const filterValue = this.addressFormGroup.controls['country'].value.toLowerCase().trim();

    this.filteredCountries = this.countries.filter((country) => country.name.toLowerCase().includes(filterValue));
    this.invalidCountry = this.filteredCountries.length === 0 ? true : false;

    this.changeDetecorRef.markForCheck();
  }

  /**
   * Dispatch the relevant action to delete the current address. Called when the user clicks onto the
   * remove button.
   */
  public removeAddress() {
    this.ngrxStore.dispatch(new MemberProfileStore.DestroyProfileAddress(this.pastDataId));
    this.closeForm();
  }

  /**
   * Close the phone number form by emitting a value that is caught by the popupFormComponent. Called when the user
   * clicks onto the close button.
   */
  public closeForm() {
    this.closed.emit();
  }

  /**
   * Checks for form validation before dispatching the relevant action to either add or update an address.
   * Called when the user clicks onto the submit button.
   */
  public submitForm() {
    this.angulartics2Segment.eventTrack(
      this.newAddress ? 'My profile user add new address' : 'My profile user edit existing address',
      {
        type: this.addressFormGroup.get('type').value,
        preferred: this.addressFormGroup.get('preferred').value,
      }
    );

    if (this.addressFormGroup.valid) {
      const data = this.addressFormGroup.value;
      this.closeForm();

      data.country = data.country_url;
      delete data.country_url;

      if (data.type !== 'other') {
        delete data.description;
      }

      if (!data.preferred) {
        delete data.preferred;
      }

      this.util.stripHTMLFromObject(data);

      if (this.newAddress) {
        this.ngrxStore.dispatch(new MemberProfileStore.CreateProfileAddress(data));
      } else {
        this.ngrxStore.dispatch(new MemberProfileStore.UpdateProfileAddress({ data, id: this.pastDataId }));
      }
    } else {
      Object.keys(this.addressFormGroup.controls).forEach((field) => {
        this.addressFormGroup.controls[field].markAsTouched();
      });
    }
  }

  public ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private setStateValidators(country: ICountry) {
    this.stateRequired = country?.state_or_province_is_required;
    const stateControl = this.addressFormGroup.controls.state;
    const stateValidators = this.stateRequired
      ? [this.noSpecialCharsValidator, Validators.required]
      : [this.noSpecialCharsValidator];

    stateControl.setValidators(stateValidators);
    stateControl.updateValueAndValidity();
  }
}
